import React, { FC, useCallback, useState } from "react";

import {
  Box,
  Flex,
  IconDownArrow,
  IconProps,
  IconSVG,
  Link,
  LinkProps,
  List,
  Popover,
  PopoverControl,
} from "@catchoftheday/cg-components";
import { minusOnePx, styled, themeGet } from "@catchoftheday/theme";

import { useIsTabletView } from "@header/hooks/useIsTabletView";
import { SelectedShop, Shop } from "@header/types";

import { trackMenuClick } from "../../utilities/analytics";
import { CATEGORY_MENU_DESKTOP_WIDTH, POPOVER_ZINDEX } from "../consts";
import { SubmenuPopover } from "../SubmenuPopover";

import { menuIconMap } from "./consts";

type MenuItemProps = LinkProps & {
  isActive: boolean;
  isSubmenuOpen?: boolean;
};

const MENU_ITEM_HEIGHT = 40;

const MenuItem = styled(Link)<MenuItemProps>`
  display: flex;
  position: relative;
  align-items: center;
  padding-left: 12px;
  height: ${MENU_ITEM_HEIGHT}px;
  color: ${(props) => themeGet("colors.text")(props)};
  font-weight: ${(props) => themeGet("fontWeights.light")(props)};
  font-size: ${(props) => themeGet("fontSizes.sm")(props)};
  background-color: ${(props) =>
    props.isSubmenuOpen && themeGet("colors.ui.backgroundColor")(props)};

  &:hover,
  &:focus,
  &:active {
    text-decoration: none;
    outline: none;
  }

  @media (min-width: ${(props) => themeGet("breakpoints.lg")(props)}) {
    width: ${CATEGORY_MENU_DESKTOP_WIDTH}px;
    border-right: 1px solid ${(props) => themeGet("colors.grey")(props)};

    &:hover,
    &:focus,
    &:active {
      background-color: ${(props) =>
        themeGet("colors.ui.backgroundColor")(props)};
      color: ${(props) => themeGet("colors.text")(props)};
      svg {
        fill: ${(props) => themeGet("colors.text")(props)};
      }

      .active-marker {
        display: block;
      }
      .right-arrow {
        visibility: visible;
      }
    }
  }

  @media (max-width: ${(props) =>
      minusOnePx(themeGet("breakpoints.lg")(props))}) and (min-width: ${(
      props
    ) => themeGet("breakpoints.md")(props)}) {
    height: 48px;
    color: ${(props) =>
      themeGet(props.isActive ? "colors.secondary" : "colors.text")(props)};
    background-color: ${(props) =>
      themeGet(props.isActive ? "colors.ui.backgroundColor" : "transparent")(
        props
      )};

    &:active,
    &:focus,
    &:hover {
      background-color: ${(props) => themeGet("colors.grey")(props)};
      color: ${(props) =>
        themeGet(props.isActive ? "colors.secondary" : "colors.text")(props)};
      svg {
        fill: ${(props) =>
          themeGet(props.isActive ? "colors.secondary" : "colors.text")(props)};
      }
    }

    ${(props) =>
      props.isActive &&
      `
      .active-marker {
        display: block;
	    }
	  `}
  }
`;

const RightArrow = styled(IconDownArrow)<IconProps>`
  transform: rotate(270deg);
  width: 9px;
`;

interface MenuItemChildrenProps {
  shop: Shop;
  menuIcon: FC<IconProps>;
  isSubmenuOpen?: boolean;
}

const MenuItemChildren = ({
  shop,
  menuIcon: MenuIcon,
  isSubmenuOpen,
}: MenuItemChildrenProps) => {
  return (
    <>
      <Box
        className="active-marker"
        display={isSubmenuOpen ? "block" : "none"}
        position="absolute"
        width="4px"
        height="100%"
        top="0px"
        left="0px"
        bg="secondary"
      />
      <Flex width="16px" height="16px" mr="8px" alignItems="center">
        <MenuIcon />
      </Flex>
      <Box flex={1}>{shop.name}</Box>
      <Box mr="15px" justifySelf="flex-end">
        <RightArrow
          className="right-arrow"
          visibility={isSubmenuOpen ? "visible" : "hidden"}
        />
      </Box>
    </>
  );
};

interface CategoryMenuProps {
  shops: Shop[];
  setCategoryMenuContainerMaxHeight?: (height: number) => void;
  setParentFocus?: () => void;
}

export const CategoryMenu = ({
  shops,
  setCategoryMenuContainerMaxHeight,
  setParentFocus,
}: CategoryMenuProps) => {
  const isTabletView = useIsTabletView();
  const [focusItemIndex, setFocusItemIndex] = useState(0);

  const keyDownHandler: React.KeyboardEventHandler<HTMLUListElement> =
    useCallback(
      (e) => {
        switch (e.code) {
          case "Home":
            e.stopPropagation();
            e.preventDefault();
            document.getElementById(`category-menu-item-0`)?.focus();
            setFocusItemIndex(0);
            break;
          case "End":
            e.stopPropagation();
            e.preventDefault();
            document
              .getElementById(`category-menu-item-${shops.length - 1}`)
              ?.focus();
            setFocusItemIndex(shops.length - 1);
            break;
          case "ArrowUp":
            e.preventDefault();
            if (typeof setParentFocus === "function" && focusItemIndex === 0) {
              setParentFocus();
            } else {
              document
                .getElementById(`category-menu-item-${focusItemIndex - 1}`)
                ?.focus();
              setFocusItemIndex(focusItemIndex - 1);
            }
            break;
          case "ArrowDown":
            e.preventDefault();
            if (focusItemIndex !== shops.length - 1) {
              document
                .getElementById(`category-menu-item-${focusItemIndex + 1}`)
                ?.focus();
              setFocusItemIndex(focusItemIndex + 1);
            }
            break;
          case "Escape":
            if (typeof setParentFocus === "function") {
              setParentFocus();
            }
            break;
          case "Tab":
            if (e.shiftKey) {
              e.preventDefault();
              document.getElementById("mini-cart")?.focus();
            }
            break;
          default:
            break;
        }
      },
      [shops.length, focusItemIndex, setParentFocus]
    );

  const setCurrentItemFocus = useCallback(() => {
    document.getElementById(`category-menu-item-${focusItemIndex}`)?.focus();
  }, [focusItemIndex]);

  const handleMenuItemClick = useCallback(
    ({ shop, position }: SelectedShop) => {
      trackMenuClick({
        name: shop.name,
        url: `${location.origin}${shop.url}/`,
        item_path: [
          {
            name: shop.name,
            id: shop.id,
            position,
          },
        ],
      });
    },
    []
  );

  return (
    <List
      overflow={isTabletView ? "scroll" : "hidden"}
      role="menu"
      m="0px"
      onKeyDown={keyDownHandler}
      ref={(element) => {
        if (setCategoryMenuContainerMaxHeight) {
          setCategoryMenuContainerMaxHeight(element?.scrollHeight ?? 0);
        }
      }}
    >
      {shops.map((shop, index) => {
        const isActive =
          typeof window !== "undefined" &&
          window.location.pathname.replace(/\/$/, "") === shop.url;

        const menuItemProps = {
          key: shop.id,
          role: "menuitem",
          href: shop.url,
          isActive,
          tabIndex: 0,
          onClick: () => handleMenuItemClick({ shop, position: index + 1 }),
        };

        const menuIcon = menuIconMap[shop.slug] || IconSVG;
        const submenuOffsetPx = `${index > 0 ? index * MENU_ITEM_HEIGHT : 0}px`;

        if (isTabletView)
          return (
            <MenuItem
              {...menuItemProps}
              aria-current={isActive ? "page" : undefined}
            >
              <MenuItemChildren shop={shop} menuIcon={menuIcon} />
            </MenuItem>
          );

        return (
          <Popover
            key={shop.id}
            placement="right-start"
            hideOnLeave
            hideOnLeaveInterval={0}
            hideArrow
            zIndex={POPOVER_ZINDEX}
            enhancedKeyboardNavigation
          >
            <PopoverControl>
              {({
                ref,
                isOpen,
                handleOnKeyDown,
                handleOnBlur,
                handleOnMouseEnter,
                handleOnMouseLeave,
              }) => (
                <MenuItem
                  {...menuItemProps}
                  tabIndex={-1}
                  aria-current={isActive ? "page" : undefined}
                  ref={ref}
                  isSubmenuOpen={isOpen}
                  onKeyDown={(e) => {
                    if (
                      e.code === "ArrowRight" ||
                      e.code === "Enter" ||
                      e.code === "Space"
                    ) {
                      e.stopPropagation();
                      e.preventDefault();
                      setTimeout(
                        () => document.getElementById(`show-all-link`)?.focus(),
                        100
                      );
                      handleOnKeyDown(e);
                    }
                  }}
                  onBlur={handleOnBlur}
                  onMouseEnter={handleOnMouseEnter}
                  onMouseLeave={handleOnMouseLeave}
                  id={`category-menu-item-${index}`}
                >
                  <MenuItemChildren
                    shop={shop}
                    menuIcon={menuIcon}
                    isSubmenuOpen={isOpen}
                  />
                </MenuItem>
              )}
            </PopoverControl>
            <Box
              position="relative"
              bottom={submenuOffsetPx}
              left={`-${CATEGORY_MENU_DESKTOP_WIDTH}px`}
            >
              <SubmenuPopover
                shop={shop}
                itemPathShop={[
                  {
                    name: shop.name,
                    id: shop.id,
                    position: index + 1,
                  },
                ]}
                setParentFocus={setCurrentItemFocus}
              />
            </Box>
          </Popover>
        );
      })}
    </List>
  );
};
